import { useMutation } from "@apollo/client";
import moment from "moment";
import {
  SubmitOneononeNewPageMutation,
  SubmitOneononeNewPageMutationVariables,
  UpdateMeetingFinalizationStatusNewPageMutationMutation,
  UpdateMeetingFinalizationStatusNewPageMutationMutationVariables,
} from "types/graphql-schema";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import Button, { ButtonProps } from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import AppPopover from "@components/popover/app-popover";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import submitOneononeMutation from "../prep/graphql/submit-oneonone-mutation";
import updateMeetingFinalizationStatusMutation from "../prep/graphql/update-meeting-finalization-status-mutation";

const MeetingFinalizeButton = ({
  meeting,
  buttonProps,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  buttonProps?: ButtonProps;
}) => {
  const currentUser = currentUserVar();

  const topics = assertEdgesNonNull(meeting.topics);
  const mandatoryTopics = topics.filter((topic) => topic.isMandatory);
  const mandatoryTopicsWithHiddenNotes = mandatoryTopics.filter((node) => {
    const hiddenIndividualNotes =
      node.individualNotes?.edges.filter(
        (edge) => edge?.node?.creator.id === currentUser.id && edge.node.hidden,
      ) || [];
    return hiddenIndividualNotes.length > 0;
  });
  const hasMandatoryTopicsWithHiddenNotes =
    mandatoryTopicsWithHiddenNotes.length > 0;

  const errorMatches = [
    {
      match: "Cannot finalize a meeting with incomplete mandatory topics",
      title: "In order to be finalized, all topics must contain notes.",
    },
  ];

  const [toggleFinalizeMeeting, { loading: loadingFinalize }] = useMutation<
    UpdateMeetingFinalizationStatusNewPageMutationMutation,
    UpdateMeetingFinalizationStatusNewPageMutationMutationVariables
  >(updateMeetingFinalizationStatusMutation);

  const handleSaveFinalizeMeeting = (newIsFinalized: boolean) => {
    const nonNullTopics = assertNonNull(meeting.topics);
    toggleFinalizeMeeting({
      variables: {
        meetingId: meeting.id,
        isFinalized: newIsFinalized,
      },
      context: { errorMatches },
      optimisticResponse: {
        __typename: "Mutation",
        updateMeetingFinalizationStatus: {
          meeting: {
            ...meeting,
            __typename: "MeetingNode",
            isFinalized: newIsFinalized,
            finalizedAt: newIsFinalized ? moment().format() : null,
            finalizedBy: newIsFinalized ? currentUser : null,
            topics: {
              ...nonNullTopics,
              __typename: "TopicNodeConnection",
              edges: nonNullTopics.edges.map((edge) => {
                const topicEdge = assertNonNull(edge);
                const topicEdgeNode = assertNonNull(topicEdge.node);
                return {
                  ...topicEdge,
                  node: {
                    ...topicEdgeNode,
                    canUpdate: {
                      ...topicEdgeNode.canUpdate,
                      permission: !newIsFinalized,
                    },
                    canDelete: {
                      ...topicEdgeNode.canDelete,
                      permission: !newIsFinalized,
                    },
                  },
                };
              }),
            },
          },
          __typename: "UpdateMeetingFinalizationStatus",
        },
      },
    });
  };

  const handleClickFinalizeMeeting = () => handleSaveFinalizeMeeting(true);

  const [submitOneonone, { loading: loadingSubmit }] = useMutation<
    SubmitOneononeNewPageMutation,
    SubmitOneononeNewPageMutationVariables
  >(submitOneononeMutation);

  const handleClickSubmit = () => {
    submitOneonone({
      variables: {
        meetingId: meeting.id,
        unhideIndividualNotes: true,
      },
    });
  };

  const meetingGroup = meeting.meetingGroup;

  if (!meetingGroup) return null;
  if (meeting.draft) return null;

  return meeting.meetingGroup.isFormalOneonone &&
    !meeting.isFinalized &&
    meeting.hasIncompleteMandatoryTopics &&
    meeting.canFinalize.permission ? (
    <AppPopover
      options={{
        className: classNames(AppPopover.className, "w-144"),
      }}
      content={
        <div className="divide-y flex flex-col">
          <div className="text-gray-700 font-semibold p-3">
            Mandatory Topics are Incomplete
          </div>
          <div className="p-3 text-gray-500 flex flex-col gap-4">
            <div>
              You must add notes to all the topics with a{" "}
              <span className="text-xs bg-amber-100 text-amber-700 px-1 py-0.5">
                Mandatory
              </span>{" "}
              tag, and make sure that they are not set to "Make private".
            </div>
            {hasMandatoryTopicsWithHiddenNotes && (
              <div>
                <Button
                  text="Reveal my notes to all participants"
                  onClick={handleClickSubmit}
                  theme={buttonTheme.lightBlue}
                  disabled={loadingSubmit}
                />
              </div>
            )}
          </div>
        </div>
      }
    >
      <AppPopover.Button
        as={Button}
        theme={buttonTheme.lightBlue}
        text="Finalize"
        {...buttonProps}
      />
    </AppPopover>
  ) : meeting.meetingGroup.isFormalOneonone &&
    !meeting.isFinalized &&
    meeting.canFinalize.permission ? (
    <Button
      theme={buttonTheme.lightBlue}
      text="Finalize"
      disabled={loadingFinalize || hasMandatoryTopicsWithHiddenNotes}
      onClick={handleClickFinalizeMeeting}
      {...buttonProps}
    />
  ) : null;
};

export default MeetingFinalizeButton;
